import React from 'react';
import PropTypes from 'prop-types';
import Modal from '@mulesoft/anypoint-components/lib/Modal';
import ModalHeader from '@mulesoft/anypoint-components/lib/ModalHeader';
import ModalBody from '@mulesoft/anypoint-components/lib/ModalBody';
import ModalFooter from '@mulesoft/anypoint-components/lib/ModalFooter';
import Button from '@mulesoft/anypoint-components/lib/Button';
import Icon from '@mulesoft/anypoint-icons/lib/Icon';
import {
  ScanConfigurationPropType,
  POLLING_INTERVAL_MS
} from '~/components/AgentScanners/types/commonTypes';
import styles from './ReviewFixModal.css';

const COMPLETED_STATUS = 'completed';

const ReviewFixModal = ({
  failedScanners,
  scanners,
  onCancel,
  onRetryScan,
  loadScanners
}) => {
  const labelId = 'review-fix-modal-title';
  const [retriedIds, setRetriedIds] = React.useState(() => new Set());
  const retryTimesRef = React.useRef({});
  const [, forceUpdate] = React.useState(0);

  const GRACE_PERIOD_MS = 5000;

  const handleRetry = (scanner) => {
    retryTimesRef.current[scanner.id] = Date.now();
    setRetriedIds((prev) => {
      const next = new Set(prev);

      next.add(scanner.id);

      return next;
    });
    onRetryScan(scanner);
  };

  React.useEffect(() => {
    if (retriedIds.size === 0 || !loadScanners) return undefined;

    const intervalId = setInterval(() => {
      loadScanners();
      forceUpdate((n) => n + 1);
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [retriedIds.size, loadScanners]);

  const isInGracePeriod = (scannerId) => {
    const retryTime = retryTimesRef.current[scannerId];

    if (!retryTime) return false;

    return Date.now() - retryTime < GRACE_PERIOD_MS;
  };

  const isScannerActive = (scannerId) => {
    const current = scanners.find((s) => s.id === scannerId);

    if (!current) return false;

    const state = (current.state || '').toLowerCase();

    return ['running', 'queued', 'importing'].includes(state);
  };

  const isScannerCompleted = (scannerId) => {
    const current = scanners.find((s) => s.id === scannerId);

    if (!current) return false;

    return (current.lastRunStatus || '').toLowerCase() === COMPLETED_STATUS;
  };

  return (
    <Modal
      renderInBody={false}
      onEsc={onCancel}
      onClickOverlay={onCancel}
      className={styles.modal}
      ariaLabelledBy={labelId}
      role="dialog"
    >
      <ModalHeader testId="review-fix-modal-header" className={styles.header}>
        <div className={styles.headerContent}>
          <h2 id={labelId} className={styles.title}>
            Review &amp; Fix
          </h2>
        </div>
      </ModalHeader>
      <ModalBody testId="review-fix-modal-body" className={styles.body}>
        <p className={styles.subtitle}>
          Fix the issues below, then retry the scan to resolve the error.
        </p>
        <ul className={styles.scannerList}>
          {failedScanners.map((scanner) => {
            const isRetried = retriedIds.has(scanner.id);
            const isFixed = isRetried && isScannerCompleted(scanner.id);
            const showRetrying =
              isRetried &&
              !isFixed &&
              (isScannerActive(scanner.id) || isInGracePeriod(scanner.id));

            if (isFixed) {
              return (
                <li
                  key={scanner.id}
                  className={styles.scannerItemWrapper}
                  data-test-id={`fixed-scanner-${scanner.id}`}
                >
                  <div className={styles.leftBarFixed} />
                  <div className={styles.fixedScannerItem}>
                    <Icon
                      name="check-small"
                      size={20}
                      className={styles.fixedIcon}
                      aria-hidden
                    />
                    <div className={styles.fixedScannerText}>
                      Fixed: {scanner.name} scan completed
                    </div>
                  </div>
                </li>
              );
            }

            if (showRetrying) {
              return (
                <li
                  key={scanner.id}
                  className={styles.scannerItemWrapper}
                  data-test-id={`retrying-scanner-${scanner.id}`}
                >
                  <div className={styles.leftBarRetrying} />
                  <div className={styles.retryingScannerItem}>
                    <span
                      className={styles.retryingSpinner}
                      style={{ animation: 'rfm-spin 0.8s linear infinite' }}
                      aria-hidden="true"
                    />
                    <div className={styles.retryingScannerText}>
                      Retrying: {scanner.name} scan in progress…
                    </div>
                  </div>
                </li>
              );
            }

            return (
              <li
                key={scanner.id}
                className={styles.scannerItemWrapper}
                data-test-id={`failed-scanner-${scanner.id}`}
              >
                <div className={styles.leftBarError} />
                <div className={styles.failedScannerItem}>
                  <Icon
                    name="error-filled"
                    size={20}
                    className={styles.failedIcon}
                    aria-hidden
                  />
                  <div className={styles.failedScannerInfo}>
                    <div className={styles.failedScannerName}>
                      Scan failed: failed to scan {scanner.name}
                    </div>
                    <div className={styles.failedScannerError}>
                      {scanner.lastRunStatusDetail ||
                        "We couldn't complete the scan. Try again later."}
                    </div>
                    <button
                      type="button"
                      className={styles.retryScanLink}
                      data-test-id={`retry-scan-${scanner.id}`}
                      onClick={() => handleRetry(scanner)}
                    >
                      Retry Scan
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </ModalBody>
      <ModalFooter testId="review-fix-modal-footer" className={styles.footer}>
        <Button kind="primary" onClick={onCancel} testId="review-fix-done">
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
};

ReviewFixModal.propTypes = {
  failedScanners: PropTypes.arrayOf(ScanConfigurationPropType),
  scanners: PropTypes.array,
  onCancel: PropTypes.func.isRequired,
  onRetryScan: PropTypes.func.isRequired,
  loadScanners: PropTypes.func
};

ReviewFixModal.defaultProps = {
  failedScanners: [],
  scanners: [],
  loadScanners: null
};

export default ReviewFixModal;
